import React, { useEffect, useState } from 'react';
import { tenantAPI } from '../../lib/api';
import { formatCurrency, formatDate, downloadBlob } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Receipt, Download, Loader2 } from 'lucide-react';

export default function TenantReceipts() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      const response = await tenantAPI.getReceipts();
      setReceipts(response.data);
    } catch (error) {
      console.error('Error loading receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (paymentId, receiptNumber) => {
    setDownloading(paymentId);
    try {
      const response = await tenantAPI.downloadReceipt(paymentId);
      downloadBlob(response.data, `receipt_${receiptNumber}.pdf`);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-receipts-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">Official Receipts</h1>
        <p className="text-muted-foreground mt-1">Download your payment receipts</p>
      </div>

      {receipts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No Receipts Yet</h3>
            <p className="text-muted-foreground">
              Receipts will appear here once your payments are verified
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {receipts.map((receipt, index) => (
            <Card key={receipt.payment_id} className="card-hover animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">
                    {receipt.receipt_number}
                  </span>
                </div>
                
                <p className="text-2xl font-bold text-[#6B8E6B] mb-2">
                  {formatCurrency(receipt.amount)}
                </p>
                
                <div className="space-y-1 text-sm text-muted-foreground mb-4">
                  <p className="capitalize">{receipt.payment_type}</p>
                  <p>Period: {receipt.period_month}</p>
                  <p>Verified: {formatDate(receipt.verified_at)}</p>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleDownload(receipt.payment_id, receipt.receipt_number)}
                  disabled={downloading === receipt.payment_id}
                  data-testid={`download-receipt-${receipt.payment_id}`}
                >
                  {downloading === receipt.payment_id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
